const BASE_URL = import.meta.env.VITE_API_URL || 'https://api.studyleague.app';

function getToken() {
  try {
    const state = JSON.parse(localStorage.getItem('auth-store') || '{}');
    return state?.token;
  } catch {
    return null;
  }
}

function unwrap(payload) {
  if (payload && Object.prototype.hasOwnProperty.call(payload, 'data')) {
    return payload.data;
  }
  return payload;
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  try {
    const authState = JSON.parse(localStorage.getItem('auth-store') || '{}');
    if (authState?.user?.activeGroupId) {
      headers['X-Group-Id'] = authState.user.activeGroupId;
    }
  } catch (e) {
    // ignore
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `HTTP ${response.status}`);
  }

  return unwrap(payload);
}

function toArray(value, key) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.[key])) return value[key];
  return [];
}

function normalizeMessage(message) {
  return {
    id: message.id,
    user_id: message.user_id ?? message.userId,
    display_name: message.display_name ?? message.displayName,
    avatar_index: message.avatar_index ?? message.avatarIndex ?? 0,
    topic_tag: message.topic_tag ?? message.topicTag,
    content: message.content,
    created_at: message.created_at ?? message.createdAt,
  };
}

function normalizeGlobalGroup(group) {
  return {
    rank: group.rank,
    groupId: group.groupId ?? group.id,
    groupName: group.groupName ?? group.name,
    joinCode: group.joinCode ?? group.join_code,
    memberCount: group.memberCount ?? group.member_count ?? 0,
    avgHours: group.avgHours ?? ((group.avgWeekSeconds ?? group.avg_weekly_seconds ?? 0) / 3600),
    topMembers: group.topMembers ?? [0, 1, 2],
  };
}

export const authApi = {
  signIn: (email, password) =>
    request('/api/auth/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  signUp: (name, email, password) =>
    request('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({
        email,
        displayName: name,
        password,
        confirmPassword: password,
      }),
    }),

  verifyOtp: async () => ({ verified: true }),
  forgotPassword: async () => {
    throw new Error('Password reset is not available yet.');
  },
};

export const usersApi = {
  getMe: () => request('/api/users/me'),

  updateMe: (data) => {
    const payload = {};
    const displayName = data.displayName ?? data.name;
    const avatarIndex = data.avatarIndex ?? data.avatar;

    if (displayName !== undefined) payload.displayName = displayName;
    if (avatarIndex !== undefined) payload.avatarIndex = avatarIndex;

    return request('/api/users/me', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  deleteAccount: async () => {
    throw new Error('Account deletion is not available yet.');
  },

  getStats: () => request('/api/users/me/stats'),
  getHeatmap: () => request('/api/users/me/heatmap').then((data) => data.heatmap ?? []),
};

export const groupsApi = {
  create: (data) =>
    request('/api/groups', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  join: (joinCode) =>
    request('/api/groups/join/request', {
      method: 'POST',
      body: JSON.stringify({ joinCode }),
    }),

  getPendingJoinRequest: () =>
    request('/api/groups/join/status'),

  cancelPendingJoinRequest: () =>
    request('/api/groups/join/request', {
      method: 'DELETE',
    }),

  leave: () =>
    request('/api/groups/leave', {
      method: 'DELETE',
    }),

  list: (search) =>
    request(`/api/groups?search=${encodeURIComponent(search || '')}`).then((data) =>
      toArray(data, 'groups').map((group) => ({
        ...group,
        joinCode: group.join_code,
        topic: group.description || 'Study',
        members: group.member_count ?? 0,
        avgHours: Math.round((group.weekly_min_seconds ?? 0) / 3600),
      }))
    ),

  getGroup: (groupId) => request(`/api/groups/${encodeURIComponent(groupId)}`),

  getMembers: (groupId) =>
    request(`/api/groups/${encodeURIComponent(groupId)}`).then((data) => data.members ?? []),

  getLive: () =>
    request('/api/sessions/live').then((data) =>
      toArray(data, 'members').map((member) => ({
        id: member.id,
        name: member.displayName ?? member.display_name,
        avatar: member.avatarIndex ?? member.avatar_index ?? 0,
        topic: member.topic,
        elapsed_sec: member.elapsedSec ?? member.elapsed_sec ?? 0,
        started_at: member.startedAt ?? member.started_at,
      }))
    ),

  updateSettings: (_groupId, data) =>
    request('/api/leader/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  assignRole: (groupId, userId, role) => {
    if (role === 'leader') {
      return request(`/api/admin/groups/${encodeURIComponent(groupId)}/leader`, {
        method: 'PUT',
        body: JSON.stringify({ userId }),
      });
    }

    return request('/api/leader/vice', {
      method: 'PUT',
      body: JSON.stringify({ userId }),
    });
  },

  removeMember: (_groupId, userId) =>
    request(`/api/leader/members/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    }),

  approveRequest: (requestId) =>
    request(`/api/leader/requests/${encodeURIComponent(requestId)}/approve`, {
      method: 'POST',
    }),

  rejectRequest: (requestId, reason) =>
    request(`/api/leader/requests/${encodeURIComponent(requestId)}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  grantEmergencyLeave: (userId) =>
    request('/api/leader/leaves/grant', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
};

export const sessionsApi = {
  save: (data) =>
    request('/api/sessions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  list: () => request('/api/sessions/week').then((data) => data.sessions ?? []),
  getLive: () => request('/api/sessions/live').then((data) => data.members ?? []),
};

export const chatApi = {
  getMessages: () =>
    request('/api/chat').then((data) => toArray(data, 'messages').map(normalizeMessage)),

  send: (_groupId, data) =>
    request('/api/chat', {
      method: 'POST',
      body: JSON.stringify(data),
    }).then((result) => ({
      ...result,
      message: result.message ? normalizeMessage(result.message) : undefined,
    })),

  pin: (_groupId, message) =>
    request('/api/leader/pin', {
      method: 'PUT',
      body: JSON.stringify({ message }),
    }),
};

export const challengesApi = {
  list: () => request('/api/challenges').then((data) => data.challenges ?? []),

  create: (data) =>
    request('/api/challenges', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: async () => {
    throw new Error('Challenge editing is not available yet.');
  },

  delete: (id) =>
    request(`/api/challenges/${encodeURIComponent(id)}/cancel`, {
      method: 'PUT',
    }),
};

export const leaderboardApi = {
  getGroup: () => request('/api/leaderboard/group').then((data) => data.rankings ?? []),

  getGlobal: (page = 1) => {
    const limit = 20;
    const offset = Math.max(0, page - 1) * limit;
    return request(`/api/leaderboard/global?limit=${limit}&offset=${offset}`).then((data) =>
      toArray(data, 'rankings').map(normalizeGlobalGroup)
    );
  },
};

export const adminApi = {
  getPendingGroups: () =>
    request('/api/admin/groups?status=pending').then((data) => data.groups ?? []),

  approveGroup: (groupId) =>
    request(`/api/admin/groups/${encodeURIComponent(groupId)}/approve`, {
      method: 'POST',
    }),

  rejectGroup: (groupId, reason) =>
    request(`/api/admin/groups/${encodeURIComponent(groupId)}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  setLeader: (groupId, userId) =>
    request(`/api/admin/groups/${encodeURIComponent(groupId)}/leader`, {
      method: 'PUT',
      body: JSON.stringify({ userId }),
    }),

  setVice: (groupId, userId) =>
    request(`/api/admin/groups/${encodeURIComponent(groupId)}/vice`, {
      method: 'PUT',
      body: JSON.stringify({ userId }),
    }),

  getStats: () =>
    request('/api/admin/stats').then((stats) => ({
      ...stats,
      total_users: stats.totalUsers ?? 0,
      total_groups: stats.totalGroups ?? 0,
      active_sessions: stats.activeRightNow ?? 0,
      total_seconds: stats.totalStudySeconds ?? 0,
    })),
};

export default {
  authApi,
  usersApi,
  groupsApi,
  sessionsApi,
  chatApi,
  challengesApi,
  leaderboardApi,
  adminApi,
};
